// Updated axios instance with better path handling

  import axios from 'axios';
  import store from '../store';
  import { logout } from '../slices/authSlice';
  
  // Cache configuration
  const CACHE_CONFIG = {
    enabled: true,           // Master switch for caching
    ttl: 60 * 60 * 1000,     // Cache TTL: 1 hour in milliseconds
    timeoutThreshold: 8000,  // Use shorter timeout for better UX
    storageKey: 'api_cache', // localStorage key for API cache
    endpoints: {
      // Key paths that should be cached
      '/api/courses': true,
      '/api/reports': true,
      '/api/groups': true,
      '/api/users': true
    }
  };

  // Replacement calculations after removing reportCalculations.js
  const getEnrollmentCount = (data) => {
    return data?.totalEnrollments || data?.total_enrollments || 0;
  };

  const getCompletedCount = (data) => {
    return data?.completedEnrollments || data?.completed_enrollments || 0;
  };

  const getCompletionRate = (data) => {
    const total = getEnrollmentCount(data);
    const completed = getCompletedCount(data);
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  // Helper to check if a response is HTML instead of JSON
  const isHtmlResponse = (data) => {
    if (typeof data === 'string' && (data.includes('<!doctype html>') || data.includes('<html'))) {
      return true;
    }
    return false;
  };

  // Determine the base URL with better fallback handling and domain normalization
  const getBaseUrl = () => {
    // First check if we have a runtime config in window.__CONFIG__
    if (window.__CONFIG__ && window.__CONFIG__.API_URL) {
      console.log('Using runtime config API URL:', window.__CONFIG__.API_URL);
      return window.__CONFIG__.API_URL;
    }

    // Next, check for env variables
    if (process.env.REACT_APP_API_BASE_URL) {
      // Always remove the trailing '/api' if it exists
      let baseUrl = process.env.REACT_APP_API_BASE_URL;
      console.log('Using env variable API URL:', baseUrl);

      // If baseUrl ends with /api, remove it to prevent double /api/api
      if (baseUrl.endsWith('/api')) {
        baseUrl = baseUrl.slice(0, -4); // Remove the last 4 characters ('/api')
      }

      // Always use the same protocol as the page
      const currentProtocol = window.location.protocol;
      // Replace the protocol in the URL with the current one
      baseUrl = baseUrl.replace(/^https?:/, currentProtocol);

      return baseUrl;
    }

    // Finally, fall back to the current origin
    // Make sure we're using the same protocol as the current page
    const protocol = window.location.protocol;
    const host = window.location.host.replace(/^www\./, ''); // Remove www if present
    const fallbackUrl = `${protocol}//${host}`;
    console.log('Using fallback API URL:', fallbackUrl);
    return fallbackUrl;
  };

  const baseURL = getBaseUrl();

  console.log('Initializing axios with baseURL:', baseURL);

  const axiosInstance = axios.create({
    baseURL,
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json'
    },
    timeout: 60000, // Increased from 10000 to 60000 (60 seconds) for better server response
    timeoutErrorMessage: "Request timed out - using cached data if available"
  });

  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  // Add a request interceptor to include the token in headers
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Don't override Content-Type for FormData
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      }

      // Ensure all API requests have /api prefix
      if (!config.url.startsWith('/api') &&
          !config.url.startsWith('/auth') &&
          !config.url.includes('/uploads/') &&
          !config.url.includes('/s3-proxy/')) {
        config.url = `/api${config.url}`;
        console.log(`Normalized API request URL to: ${config.url}`);
      }

      // Handle authentication URLs specifically
      if (config.url === '/auth/login' || config.url === '/auth/register') {
        // Add /api prefix for auth endpoints if not already included
        if (!config.url.startsWith('/api')) {
          config.url = `/api${config.url}`;
        }
        console.log(`Auth endpoint remapped to: ${config.url}`);
      }

      // Fix double /api prefix issue which can occur with admin report endpoints
      if (config.url.includes('/api/api/')) {
        config.url = config.url.replace('/api/api/', '/api/');
        console.log(`Fixed double API prefix: ${config.url}`);
      }

      // Log API requests in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
      }

      return config;
    },
    (error) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // Add a response interceptor for error handling
  axiosInstance.interceptors.response.use(
    (response) => {
      // Check if response is HTML instead of expected JSON
      if (response.data && isHtmlResponse(response.data)) {
        console.error('Received HTML response for API request:', {
          url: response.config.url,
          method: response.config.method
        });

        // Convert to error for proper handling
        const error = new Error('Received HTML instead of JSON response');
        error.response = response;
        error.isHtmlResponse = true;
        return Promise.reject(error);
      }

      // Handle authentication success case explicitly
      if (response.config.url.includes('/auth/login') && response.data?.token) {
        console.log('Authentication successful, storing token');
        localStorage.setItem('token', response.data.token);
      }

      // Process any report/metrics data to ensure correct completion rates
      const isReportEndpoint = response.config.url.includes('/reports/') ||
                              response.config.url.includes('/admin/reports');

      // Only process report endpoints
      if (isReportEndpoint) {
        // Check for metrics in the response
        if (response.data?.metrics) {
          fixMetricsData(response.data.metrics);
        }

        // Check for specific metrics endpoints
        if (response.config.url.includes('/metrics') && response.data?.success) {
          fixMetricsData(response.data);
        }

        // For array results (like course listings)
        if (response.data?.results && Array.isArray(response.data.results)) {
          response.data.results.forEach(item => {
            if (isMetricsObject(item)) {
              fixMetricsData(item);
            }
          });
        }
      }

      return response;
    },
    async (error) => {
      // Check if the error is due to an HTML response
      if (error.isHtmlResponse) {
        console.error('Server returned HTML instead of JSON. This could be a routing issue on the server.');

        // For course fetching, we need to handle specially
        if (error.response?.config?.url?.includes('/courses/')) {
          console.log('Attempting fallback for course fetch...');

          // If we're trying to fetch a course by ID, return a more helpful error
          const courseId = error.response.config.url.match(/\/courses\/(\d+)/)?.[1];
          if (courseId) {
            return Promise.reject({
              response: {
                status: 500,
                data: {
                  message: `Server routing error: Received HTML instead of course data for course ID ${courseId}. Please check server configuration.`
                }
              }
            });
          }
        }
      }

      // Add more detailed error logging
      const errorDetails = {
        message: error.message,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        responseData: error.response?.data
      };

      console.error('API Error Details:', errorDetails);

      const originalRequest = error.config;

      // Retry for timeout errors
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout') && !originalRequest._retry) {
        console.log(`Request timeout for ${originalRequest.url}, attempting retry with longer timeout`);
        originalRequest._retry = true;
        originalRequest.timeout = 120000; // Increase timeout to 2 minutes for the retry
        return axiosInstance(originalRequest);
      }

      // Handle 401 unauthorized errors
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // If token refresh is in progress, queue the request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => axiosInstance(originalRequest))
            .catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          console.log('Unauthorized access detected. Logging out user...');
          // Clean up auth state
          store.dispatch(logout());
          // Clear all storage
          localStorage.clear();
          sessionStorage.clear();

          // Only redirect to login if not already on a public route
          const publicRoutes = ['/login', '/register', '/signup'];
          if (!publicRoutes.some(route => window.location.pathname.includes(route))) {
            console.log('Redirecting to login page...');
            window.location.replace('/login');
          }

          processQueue(null);
          return Promise.reject(error);
        } catch (refreshError) {
          console.error('Error during logout process:', refreshError);
          processQueue(refreshError);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Don't show auth errors for public routes
      const publicRoutes = ['/login', '/register', '/signup'];
      const isPublicRoute = publicRoutes.some(route =>
        window.location.pathname.includes(route)
      );

      if (isPublicRoute && error.response?.status === 401) {
        return Promise.reject(error);
      }

      // Handle 403 Forbidden errors with specific flags
      if (error.response?.status === 403) {
        const isModuleRequest = originalRequest.url.includes('/modules');
        const customError = new Error(
          isModuleRequest
            ? 'You do not have permission to view this course modules.'
            : error.response.data?.message || 'Access denied'
        );
        customError.isAccessError = true;
        customError.isModuleError = isModuleRequest;
        customError.status = 403;
        customError.originalError = error;

        console.error('Access Error:', {
          endpoint: originalRequest?.url,
          isModuleError: isModuleRequest,
          status: 403,
          message: customError.message,
          details: error.response?.data?.message
        });

        return Promise.reject(customError);
      }

      // Handle network errors explicitly
      if (error.message === 'Network Error') {
        const networkError = new Error('Unable to connect to the server. Please check your internet connection and try again.');
        networkError.isNetworkError = true;
        networkError.originalError = error;
        networkError.endpoint = originalRequest?.url;

        console.error('Network Error:', {
          endpoint: originalRequest?.url,
          message: 'Unable to connect to the server'
        });

        // Try to use cached data for network errors
        const url = originalRequest?.url;
        if (url && ApiCache.shouldCache(url)) {
          const cachedData = ApiCache.get(url);
          if (cachedData) {
            console.log(`Using cached data as fallback for network error at ${url}`);
            return Promise.resolve({
              data: cachedData,
              status: 200,
              statusText: 'OK (Cached during network error)',
              headers: {},
              config: originalRequest,
              cached: true,
              originalError: error
            });
          }
        }

        return Promise.reject(networkError);
      }

      return Promise.reject(error);
    }
  );

  /**
   * Check if an object is likely to be a metrics object
   */
  function isMetricsObject(obj) {
    if (!obj || typeof obj !== 'object') return false;

    // Check for common metrics properties
    return obj.totalEnrollments !== undefined ||
           obj.total_enrollments !== undefined ||
           obj.completedEnrollments !== undefined ||
           obj.completed_enrollments !== undefined ||
           obj.completionRate !== undefined ||
           obj.completion_rate !== undefined;
  }

  /**
   * Fix metrics data by ensuring completion rate is calculated correctly
   */
  function fixMetricsData(data) {
    if (!data || typeof data !== 'object') return;

    // Extract enrollment counts
    const totalEnrollments = getEnrollmentCount(data);
    const completedEnrollments = getCompletedCount(data);

    // Always recalculate completion rate
    const calculatedRate = getCompletionRate({
      totalEnrollments,
      completedEnrollments
    });

    // Apply the correct completion rate
    data.completionRate = calculatedRate;
    data.completion_rate = calculatedRate;

    // Fix metrics object if it exists
    if (data.metrics) {
      data.metrics.completionRate = calculatedRate;
      data.metrics.completion_rate = calculatedRate;
    }

    // Add flag to indicate the data was fixed
    data._fixed = true;
  }

  // Cache management functions
  const ApiCache = {
    // Get the entire cache
    getCache() {
      try {
        const cacheData = localStorage.getItem(CACHE_CONFIG.storageKey);
        return cacheData ? JSON.parse(cacheData) : {};
      } catch (error) {
        console.error('Error reading cache:', error);
        return {};
      }
    },
    
    // Save the entire cache
    saveCache(cache) {
      try {
        localStorage.setItem(CACHE_CONFIG.storageKey, JSON.stringify(cache));
      } catch (error) {
        console.error('Error saving cache:', error);
      }
    },
    
    // Get a specific cache entry
    get(key) {
      const cache = this.getCache();
      const cacheEntry = cache[key];
      
      // Return null if no cache entry or expired
      if (!cacheEntry || (Date.now() - cacheEntry.timestamp > CACHE_CONFIG.ttl)) {
        return null;
      }
      
      return cacheEntry.data;
    },
    
    // Set a cache entry
    set(key, data) {
      const cache = this.getCache();
      cache[key] = {
        data,
        timestamp: Date.now()
      };
      this.saveCache(cache);
    },
    
    // Clear a specific cache entry
    clear(key) {
      const cache = this.getCache();
      delete cache[key];
      this.saveCache(cache);
    },
    
    // Clear all cache
    clearAll() {
      localStorage.removeItem(CACHE_CONFIG.storageKey);
    },
    
    // Should this endpoint be cached?
    shouldCache(url) {
      if (!CACHE_CONFIG.enabled) return false;
      
      // Check if the URL matches any of our cacheable endpoints
      return Object.keys(CACHE_CONFIG.endpoints).some(endpoint => {
        return url.startsWith(endpoint);
      });
    }
  };
  
  // Add the cache to the axios instance for external use
  axiosInstance.cache = ApiCache;
  
  // Create a cached version of the get method
  const originalGet = axiosInstance.get;
  axiosInstance.get = function(url, config = {}) {
    // Normalize the URL for consistent cache keys
    const normalizedUrl = axiosInstance.normalizePath(url);
    
    // If we shouldn't cache this URL, just use the original method
    if (!ApiCache.shouldCache(normalizedUrl)) {
      return originalGet(url, config);
    }
    
    // Apply the shorter timeout for better UX
    const mergedConfig = {
      ...config,
      timeout: config.timeout || CACHE_CONFIG.timeoutThreshold
    };
    
    // Use the cache first if available
    const cachedData = ApiCache.get(normalizedUrl);
    if (cachedData) {
      console.log(`Using cached data for ${normalizedUrl}`);
      
      // Return cached data as a resolved promise
      return Promise.resolve({
        data: cachedData,
        status: 200,
        statusText: 'OK (Cached)',
        headers: {},
        config: mergedConfig,
        cached: true
      });
    }
    
    // If no cache, make the actual request
    return originalGet(url, mergedConfig)
      .then(response => {
        // Cache the successful response
        ApiCache.set(normalizedUrl, response.data);
        return response;
      })
      .catch(error => {
        console.error(`API request failed for ${normalizedUrl}:`, error.message);
        
        // If we have cached data, use it as fallback
        const cachedData = ApiCache.get(normalizedUrl);
        if (cachedData) {
          console.log(`Using cached data as fallback for failed request to ${normalizedUrl}`);
          return {
            data: cachedData,
            status: 200,
            statusText: 'OK (Cached Fallback)',
            headers: {},
            config: mergedConfig,
            cached: true,
            originalError: error
          };
        }
        
        // If no cache is available, rethrow the error
        throw error;
      });
  };

  // Normalize URL paths
  axiosInstance.normalizePath = (path) => {
    // Ensure path starts with /
    if (!path.startsWith('/')) {
      path = '/' + path;
    }

    // Ensure /api prefix for API endpoints
    if (!path.startsWith('/api') &&
        !path.startsWith('/auth') &&
        !path.includes('/uploads/')) {
      path = '/api' + path;
    }

    // Ensure /api prefix for auth endpoints
    if ((path.startsWith('/auth/') || path === '/auth') && !path.startsWith('/api/auth')) {
      path = '/api' + path;
    }

    return path;
  };

  export default axiosInstance;